import { useState } from "react";
import { ConfirmationWrapper } from "./style";
import { useDispatch } from "react-redux";

// Components
import Text from "components/common/Text";
import CodeInput from "components/common/CodeInput";
import Button from "components/common/Button";
import Image from "next/image";
import CountDown from "components/common/CountDown";
import SignUp from "./SignUp";
import CreateProfile from "./CreateProfile";

// Libs
import Translation from "libs/Translation";
import Util from "libs/Util";
import ToastManager from "libs/ToastManager";
import ModalManager from "libs/ModalManager";

// Redux
import { setUserAction, setAuthenticationAction } from "redux/user/actions";
import { postAPI, getAPI } from "redux/core/actions";

const ConfirmationCode = ({ webConfig, setStep, phoneNumber }) => {
  const dispatch = useDispatch();
  const optLimitTime = Util.setFutureTimer(120);
  const [code, setCode] = useState([]);
  const [loading, setLoading] = useState();
  const [codeLoading, setCodeLoading] = useState();
  const [error, setError] = useState();

  const handleChange = (value) => {
    setCode(value);
    setError(false);
  };

  const doConfirm = async (valueStr) => {
    setLoading(true);

    dispatch(
      postAPI(
        "confirmationCode",
        {
          phoneNumber: phoneNumber?.phoneNumber ?? phoneNumber,
          otp: Util.convertToEngNum(valueStr),
        },
        null,
        "nestApi"
      )
    )
      .then(async (response) => {
        // Nest returns: { user, jwtToken }
        const token = response?.jwtToken;
        const user = response?.user;

        if (token) {
          await dispatch(setAuthenticationAction({ accessToken: token }));

          if (user) {
            const phone = user?.PhoneNumber
              ? `98${String(user.PhoneNumber).slice(1)}`
              : undefined;

            if (
              Array.isArray(user?.PatientProfiles) &&
              user.PatientProfiles.length > 0
            ) {
              await dispatch(
                setUserAction({
                  userId: user.id || user._id,
                  phone,
                  profiles: user.PatientProfiles,
                })
              );
              ToastManager.show(Translation.t("text.logged-in"), webConfig);
              ModalManager.hide();
              return;
            }

            // No profiles → go to CreateProfile
            await dispatch(
              setUserAction({
                userId: user.id || user._id,
                phone,
              })
            );
            setStep({
              name: "createProfile",
              component: <CreateProfile webConfig={webConfig} />,
            });
            return;
          }
        }
      })
      .catch((err) => {
        console.error(err);
        if (err.message === "StatusUnauthorized") {
          ToastManager.show(
            Translation.t("text.the-code-is-incorrect"),
            webConfig
          );
          setError(true);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleConfirm = (value) => doConfirm(value);
  const handleSubmit = (e) => {
    e.preventDefault();
    // ensure submit uses current code
    if (
      Array.isArray(code) ? code.length >= 6 : String(code || "").length >= 6
    ) {
      doConfirm(Array.isArray(code) ? code.join("") : String(code));
    }
  };

  const handleResendCode = () => {
    setCodeLoading(true);

    dispatch(postAPI("signUp", phoneNumber, null, "nestApi"))
      .then((response) => {
        if (response === true) {
          ToastManager.show(
            Translation.t("text.the-code-is-resend"),
            webConfig
          );
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setCodeLoading(false);
      });
  };

  const handleGoPrevious = () => {
    setStep({
      name: "signUp",
      component: <SignUp webConfig={webConfig} setStep={setStep} />,
    });
  };

  return (
    <ConfirmationWrapper onSubmit={handleSubmit}>
      <Image
        src="/nextasset/images/Logo.svg"
        width={155}
        height={59}
        alt="vclinic-logo"
      />
      <div className="row-flex padding-15">
        <Image
          src="/nextasset/images/get-appointment/edit.svg"
          alt="pen-icon"
          width={24}
          height={24}
          onClick={handleGoPrevious}
          style={{ cursor: "pointer" }}
        />
        <span className="phone">
          +{Translation.n(phoneNumber.countryCode)}{" "}
          {Translation.n(phoneNumber.phoneNumber)}
        </span>
      </div>
      <Text className="desc" tag="span">
        {Translation.t("label.enter-sent-code")}
      </Text>
      <div className="input">
        <CodeInput
          onChange={handleChange}
          onComplete={handleConfirm}
          error={error}
          code={code}
        />
      </div>
      <form className="button-container">
        <CountDown
          onClick={handleResendCode}
          loading={codeLoading}
          nextRetry={optLimitTime}
          retryButton
        />
        <Button
          className="submit-button"
          loading={loading}
          disabled={code.length < 6}
          type="submit"
        >
          {Translation.t("label.next-step")}
        </Button>
      </form>
    </ConfirmationWrapper>
  );
};
export default ConfirmationCode;
